/**
 * ActionButtonsMenu Component - iOS Action Sheet Pattern
 *
 * Mobile: Bottom sheet that slides up from bottom
 * Desktop: Dropdown menu positioned near button
 * Both: Full backdrop with tap-to-dismiss
 */

import React, { useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';

interface ActionButtonsMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  isDeletingComparison: boolean;
  isFavorited: boolean;
  onFavorite: () => void;
  onCopy: () => void;
  onReprompt: () => void;
  onDelete: () => void;
}

export const ActionButtonsMenu: React.FC<ActionButtonsMenuProps> = ({
  isOpen,
  onToggle,
  onClose,
  isDeletingComparison,
  isFavorited,
  onFavorite,
  onCopy,
  onReprompt,
  onDelete,
}) => {
  // Escape key closes menu
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll on mobile when menu open
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const handleActionClick = (action: () => void) => {
    action();
    onClose();
  };

  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // Desktop-only outside click handler to avoid backdrop intercepting menu clicks
  useEffect(() => {
    if (!isOpen) return;
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    if (!mediaQuery.matches) return; // mobile uses backdrop tap to dismiss

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const menuContent = isOpen ? (
    <>
      {/* Backdrop - tap anywhere to close */}
      <div
        className="fixed inset-0 bg-black/40 z-[60] md:bg-transparent md:pointer-events-none md:z-[100]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Sheet/Dropdown */}
      <div
        role="dialog"
        aria-label="Comparison actions"
        className="
          fixed bottom-0 left-0 right-0 z-[70]
          md:absolute md:top-full md:right-0 md:left-auto md:bottom-auto
          md:mt-2 md:w-64 md:z-[110]
          bg-slate-900 rounded-t-2xl md:rounded-lg
          border-t md:border border-slate-700/60
          shadow-2xl
          pb-safe
        "
      >
        {/* Drag handle (mobile only) */}
        <div className="w-12 h-1 bg-slate-600 rounded-full mx-auto mt-3 mb-4 md:hidden" />

        {/* Action buttons */}
        <div className="px-4 pb-6 space-y-2 md:p-2 md:space-y-1">
          {/* Favorite */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(onFavorite);
            }}
            className="
              flex items-center gap-4 md:gap-3
              w-full min-h-[44px] md:min-h-0
              px-4 py-3 md:px-3 md:py-2
              text-base md:text-sm font-medium
              rounded-xl md:rounded-lg
              text-slate-100 hover:bg-slate-800/80 active:bg-slate-800
              transition-all active:scale-[0.98]
            "
          >
            <svg
              className={`w-6 h-6 md:w-5 md:h-5 flex-shrink-0 ${
                isFavorited ? 'fill-yellow-400 stroke-yellow-400' : 'fill-none stroke-slate-300'
              }`}
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
              />
            </svg>
            <span>{isFavorited ? 'Unfavorite' : 'Favorite'}</span>
          </button>

          {/* Copy Link */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(onCopy);
            }}
            className="
              flex items-center gap-4 md:gap-3
              w-full min-h-[44px] md:min-h-0
              px-4 py-3 md:px-3 md:py-2
              text-base md:text-sm font-medium
              rounded-xl md:rounded-lg
              text-slate-100 hover:bg-slate-800/80 active:bg-slate-800
              transition-all active:scale-[0.98]
            "
          >
            <svg
              className="w-6 h-6 md:w-5 md:h-5 flex-shrink-0 text-slate-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2" />
              <rect x="10" y="8" width="10" height="12" rx="2" ry="2" />
            </svg>
            <span>Copy Link</span>
          </button>

          {/* Reprompt */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(onReprompt);
            }}
            className="
              flex items-center gap-4 md:gap-3
              w-full min-h-[44px] md:min-h-0
              px-4 py-3 md:px-3 md:py-2
              text-base md:text-sm font-medium
              rounded-xl md:rounded-lg
              text-slate-100 hover:bg-slate-800/80 active:bg-slate-800
              transition-all active:scale-[0.98]
            "
          >
            <ArrowPathIcon className="w-6 h-6 md:w-5 md:h-5 flex-shrink-0 text-slate-300" />
            <span>Reprompt</span>
          </button>

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(onDelete);
            }}
            disabled={isDeletingComparison}
            className="
              flex items-center gap-4 md:gap-3
              w-full min-h-[44px] md:min-h-0
              px-4 py-3 md:px-3 md:py-2
              text-base md:text-sm font-medium
              rounded-xl md:rounded-lg
              text-red-300 hover:bg-red-900/30 active:bg-red-900/40
              disabled:opacity-60 disabled:cursor-not-allowed
              transition-all active:scale-[0.98]
            "
          >
            {isDeletingComparison ? (
              <svg
                className="w-6 h-6 md:w-5 md:h-5 flex-shrink-0 animate-spin text-red-200"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 12a8 8 0 018-8" />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 md:w-5 md:h-5 flex-shrink-0 text-red-300"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m-3 0h14"
                />
              </svg>
            )}
            <span>{isDeletingComparison ? 'Deleting...' : 'Delete'}</span>
          </button>
        </div>
      </div>
    </>
  ) : null;

  return (
    <div className="relative">
      {/* Ellipsis button */}
      <button
        ref={triggerRef}
        onClick={onToggle}
        className="group p-2 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 rounded-lg text-slate-300 hover:text-white transition-colors"
        aria-label="More actions"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
      </button>

      {/* Render menu in portal on mobile, inline on desktop */}
      {typeof window !== 'undefined' && menuContent && (
        <>
          {/* Mobile: Portal to body */}
          <div className="md:hidden">{createPortal(menuContent, document.body)}</div>
          {/* Desktop: Inline */}
          <div className="hidden md:block" ref={menuRef}>{menuContent}</div>
        </>
      )}
    </div>
  );
};
